import React, { useState, useRef, useEffect } from "react";
import { motion, useMotionValue, useTransform, useAnimation, PanInfo, AnimatePresence } from "framer-motion";
import { PROVIDER_CLIPS, pickRandom, type Clip } from "../config/providerVideos";
import doneVideo from "../assets/done-video.mp4";
import navbarImg from "../assets/navbar.png";
import { ArrowRight, RotateCcw, ArrowLeft, Volume2, VolumeX } from "lucide-react";
import { Button } from "./ui/button";
import { DemoRequestDialog } from "./DemoRequestDialog";
import handImg from "../assets/hand.png";

interface Card {
    id: number;
    video: string;
    category: string;
    title: string;
}

const DECK_SIZE = 9;

/** A fresh random deck of clips each time the mockup mounts. */
const buildDeck = (clips: readonly Clip[]): Card[] => pickRandom(clips, DECK_SIZE).map((clip, i) => ({ id: i + 1, video: clip.src, category: clip.category, title: clip.title }));

interface QuiltSwipeAppProps {
    onAnalysisComplete?: (category: string) => void;
    onReset?: () => void;
    customCtaText?: string;
    onCustomCtaClick?: () => void;
    /** Clips to deal the deck from; the provider clips when left out */
    clips?: readonly Clip[];
}

const QuiltSwipeApp: React.FC<QuiltSwipeAppProps> = ({ onAnalysisComplete, onReset, customCtaText, onCustomCtaClick, clips = PROVIDER_CLIPS }) => {
    // Random deck; reversed so deck[0] sits at cards.length - 1 (the top card rendered and swiped first)
    const [deck] = useState<Card[]>(() => buildDeck(clips));
    const [cards, setCards] = useState<Card[]>(() => [...deck].reverse());
    const [complete, setComplete] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [likedCategories, setLikedCategories] = useState<string[]>([]);
    const [isMuted, setIsMuted] = useState(true);
    const [currentProgress, setCurrentProgress] = useState(0);

    const currentStoryIndex = deck.length - cards.length;

    const reset = () => {
        setCards([...deck].reverse());
        setComplete(false);
        setHasInteracted(false);
        setLikedCategories([]);
        setCurrentProgress(0);
        if (onReset) onReset();
    };

    const handleSwipe = (direction: 'left' | 'right') => {
        const swipedCard = cards[cards.length - 1]; // The top card
        if (swipedCard && direction === 'right') {
            setLikedCategories(prev => [...prev, swipedCard.category]);
        }
        setCurrentProgress(0);
        setCards((prev) => prev.slice(0, prev.length - 1));
    };

    useEffect(() => {
        if (cards.length === 0) {
            setComplete(true);
            const dominantCategory = likedCategories.length > 0
                ? likedCategories[likedCategories.length - 1]
                : "Balanced";
            if (onAnalysisComplete) {
                setTimeout(() => onAnalysisComplete(dominantCategory), 500);
            }
        }
    }, [cards, likedCategories, onAnalysisComplete]);

    return (
        <div
            className="w-full h-full relative font-sans bg-white dark:bg-black select-none overflow-hidden"
        >
            <AnimatePresence mode="wait">
                {complete ? (
                    <motion.div
                        key="completion"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="w-full h-full"
                    >
                        <CompletionScreen onReset={reset} customCtaText={customCtaText} onCustomCtaClick={onCustomCtaClick} />
                    </motion.div>
                ) : (
                    <motion.div
                        key="cards"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.05 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        className="w-full h-full relative"
                    >
                        {/* Instagram-style Story Progress Bars on Top */}
                        <div className="absolute top-11 left-0 right-0 z-40 px-3 flex items-center gap-1 pointer-events-none">
                            {deck.map((_, idx) => {
                                let widthPercent = 0;
                                if (idx < currentStoryIndex) {
                                    widthPercent = 100;
                                } else if (idx === currentStoryIndex) {
                                    widthPercent = Math.min(Math.max(currentProgress * 100, 0), 100);
                                } else {
                                    widthPercent = 0;
                                }

                                return (
                                    <div
                                        key={idx}
                                        className="h-[3px] flex-1 bg-white/30 backdrop-blur-sm rounded-full overflow-hidden shadow-sm"
                                    >
                                        <div
                                            className="h-full bg-white rounded-full transition-all duration-75 ease-linear"
                                            style={{ width: `${widthPercent}%` }}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {/* Instagram-style Mute/Unmute Button on Right Side (no circle) */}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setIsMuted((prev) => !prev);
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            className="absolute top-14 right-3.5 z-50 p-1 before:absolute before:-inset-2 before:content-[''] text-white hover:opacity-80 active:scale-90 transition-all flex items-center justify-center cursor-pointer focus:outline-none"
                            aria-label={isMuted ? "Unmute video audio" : "Mute video audio"}
                            title={isMuted ? "Unmute" : "Mute"}
                        >
                            {isMuted ? (
                                <VolumeX className="w-5 h-5 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
                            ) : (
                                <Volume2 className="w-5 h-5 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
                            )}
                        </button>

                        {/* Card Stack */}
                        {cards.map((card, index) => (
                            <SwipeCard
                                key={card.id}
                                card={card}
                                isTop={index === cards.length - 1}
                                preload={index === cards.length - 1 ? "auto" : index === cards.length - 2 ? "metadata" : "none"}
                                isMuted={isMuted}
                                onSwipe={(dir) => {
                                    setHasInteracted(true);
                                    handleSwipe(dir);
                                }}
                                onDragStart={() => setHasInteracted(true)}
                                onProgressUpdate={setCurrentProgress}
                            />
                        ))}

                        {/* Helper Overlay (Animated Swipe Left/Right Hand) */}
                        <AnimatePresence>
                            {!hasInteracted && !complete && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.5, delay: 0.5 }}
                                    className="absolute inset-0 z-40 flex flex-col items-center justify-center pointer-events-none bg-black/40"
                                >
                                    <div className="relative flex items-center justify-center">
                                        <motion.div
                                            animate={{ x: [-10, 10, -10] }}
                                            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                                            className="relative z-10"
                                        >
                                            <img src={handImg} alt="Swipe Hand" className="w-16 h-16 drop-shadow-lg opacity-90" />
                                        </motion.div>

                                        {/* Arrows */}
                                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 flex justify-between items-center px-4">
                                            <motion.div
                                                animate={{ x: [-5, 0, -5], opacity: [0.5, 1, 0.5] }}
                                                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                                            >
                                                <ArrowLeft className="w-8 h-8 text-white drop-shadow-md" />
                                            </motion.div>
                                            <motion.div
                                                animate={{ x: [5, 0, 5], opacity: [0.5, 1, 0.5] }}
                                                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                                            >
                                                <ArrowRight className="w-8 h-8 text-white drop-shadow-md" />
                                            </motion.div>
                                        </div>
                                    </div>
                                    <p className="text-white/90 font-medium mt-4 text-shadow-sm">Swipe to see more..</p>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Tab Bar Overlay */}
                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
                            <div className="bg-black/60 backdrop-blur rounded-full px-2 py-1 border border-white/10 shadow-lg ">
                                <img src={navbarImg} alt="Navigation Bar" className="h-6 w-auto opacity-80 object-contain" />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

interface SwipeCardProps {
    card: Card;
    isTop: boolean;
    preload: "auto" | "metadata" | "none";
    isMuted: boolean;
    onSwipe: (direction: 'left' | 'right') => void;
    onDragStart?: () => void;
    onProgressUpdate?: (progress: number) => void;
}

const SwipeCard: React.FC<SwipeCardProps> = ({ card, isTop, preload, isMuted, onSwipe, onDragStart, onProgressUpdate }) => {
    const controls = useAnimation();
    const x = useMotionValue(0);
    const rotate = useTransform(x, [-200, 200], [-15, 15]);
    const opacity = useTransform(x, [-200, -150, 0, 150, 200], [0, 1, 1, 1, 0]);

    // Swipe Gradients
    const rightOpacity = useTransform(x, [0, 150], [0, 0.6]);
    const leftOpacity = useTransform(x, [-150, 0], [0.6, 0]);

    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (isTop && videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.muted = isMuted;
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(e => console.log("Autoplay handled", e));
            }
        } else if (videoRef.current) {
            videoRef.current.pause();
        }
    }, [isTop]);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = isMuted;
        }
    }, [isMuted]);

    const handleTimeUpdate = () => {
        if (isTop && videoRef.current && onProgressUpdate) {
            const current = videoRef.current.currentTime;
            const duration = videoRef.current.duration;
            if (duration && duration > 0) {
                onProgressUpdate(current / duration);
            }
        }
    };

    const handleDragEnd = async (_: any, info: PanInfo) => {
        const offset = info.offset.x;
        const velocity = info.velocity.x;

        if (Math.abs(offset) > 100 || Math.abs(velocity) > 500) {
            const direction = offset > 0 ? 1 : -1;
            await controls.start({ x: direction * 500, opacity: 0, transition: { duration: 0.4 } });
            onSwipe(direction === 1 ? 'right' : 'left');
        } else {
            controls.start({ x: 0, opacity: 1, transition: { type: "spring", stiffness: 300, damping: 20 } });
        }
    };

    return (
        <motion.div
            drag={isTop ? "x" : false}
            dragConstraints={{ left: 0, right: 0 }}
            onDragStart={onDragStart}
            style={{ x, rotate, opacity, zIndex: isTop ? 10 : 1 }}
            animate={controls}
            onDragEnd={handleDragEnd}
            className="absolute top-0 left-0 w-full h-full rounded-[2rem] overflow-hidden shadow-[0_35px_60px_-15px_rgba(0,0,0,0.8)] origin-bottom"
        >
            <video
                ref={videoRef}
                className="w-full h-full object-cover pointer-events-none rounded-[2rem] overflow-hidden"
                playsInline
                preload={preload}
                muted={isMuted}
                loop
                onTimeUpdate={handleTimeUpdate}
            >
                {/* the storage server labels clips binary/octet-stream; the type keeps Safari happy */}
                <source src={card.video} type="video/mp4" />
            </video>

            {/* Top gradient shadow for story bars & controls readability */}
            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/70 via-black/30 to-transparent z-10 pointer-events-none rounded-t-[2rem]" />

            {/* Green Overlay (Right Swipe - Like/Green) */}
            <motion.div
                style={{ opacity: rightOpacity }}
                className="absolute inset-0 bg-green-500/40 z-10 pointer-events-none mix-blend-overlay"
            />
            <motion.div
                style={{ opacity: rightOpacity }}
                className="absolute inset-0 bg-gradient-to-l from-green-500/60 to-transparent z-10 pointer-events-none"
            />

            {/* Red Overlay (Left Swipe - Nope/Red) */}
            <motion.div
                style={{ opacity: leftOpacity }}
                className="absolute inset-0 bg-red-500/40 z-10 pointer-events-none mix-blend-overlay"
            />
            <motion.div
                style={{ opacity: leftOpacity }}
                className="absolute inset-0 bg-gradient-to-r from-red-500/60 to-transparent z-10 pointer-events-none"
            />

            {/* Gradient overlay for text visibility */}
            <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-black/80 to-transparent pointer-events-none z-10" />

            {/* <div className="absolute bottom-20 left-6 right-6 pointer-events-none z-20">
                <h3 className="text-white text-2xl font-bold drop-shadow-lg mb-2">Daily Inspiration</h3>
                <p className="text-white/80 text-sm drop-shadow-md">Swipe to see more...</p>
            </div> */}
        </motion.div>
    );
};

interface CompletionScreenProps {
    onReset: () => void;
    customCtaText?: string;
    onCustomCtaClick?: () => void;
}

const CompletionScreen: React.FC<CompletionScreenProps> = ({ onReset, customCtaText, onCustomCtaClick }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.play().catch(e => console.log("Autoplay blocked", e));
        }
    }, []);

    return (
        <div className="w-full h-full flex flex-col items-center justify-center p-6 bg-neutral-950 animate-in fade-in duration-500 relative">
            <div className="w-full flex-1 flex flex-col items-center justify-center space-y-6 mt-12">
                <h2 className="text-2xl font-bold text-white tracking-tight">Daily Dose Complete</h2>
                <div className="w-40 h-40 rounded-full overflow-hidden shadow-xl relative">
                    <video
                        ref={videoRef}
                        src={doneVideo}
                        className="w-full h-full object-cover"
                        playsInline
                        muted
                        loop
                    />
                </div>
            </div>

            <div className="w-full space-y-3 mb-10">
                {customCtaText && onCustomCtaClick ? (
                    <Button
                        onClick={onCustomCtaClick}
                        className="btn-primary text-white w-full h-auto py-4 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-semibold transition-all shadow-lg shadow-emerald-900/20 active:scale-95 text-base"
                    >
                        {customCtaText} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                ) : (
                    <DemoRequestDialog triggerText="See Full Demo" source="provider_schedule_demo">
                        <Button
                            className="btn-primary text-white w-full h-auto py-4 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-semibold transition-all shadow-lg shadow-emerald-900/20 active:scale-95 text-base"
                        >
                            See Full Demo <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </DemoRequestDialog>

                )}

                <button
                    onClick={onReset}
                    className="w-full py-3 text-gray-400 hover:text-white font-medium transition-colors flex items-center justify-center gap-2"
                >
                    <RotateCcw className="w-4 h-4" /> Try again
                </button>
            </div>
        </div>
    );
};

export default QuiltSwipeApp;

